import { Toaster } from "@/components/ui/toaster";
import { ServiceWorkerUpdateToast } from "@/components/ServiceWorkerUpdateToast";
import { TooltipProvider } from "@radix-ui/react-tooltip"; // Usar TooltipProvider de Radix UI directamente
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from '@/pages/Index';
import OtpLoginPage from '@/pages/OtpLoginPage';
import MainLayout from '@/components/MainLayout';
import { ThemeProvider } from '@/components/ThemeProvider';
import { NotificationProvider as AppNotificationProvider } from '@/context/NotificationContext';
import { CheckoutProvider } from '@/context/CheckoutContext';
import { SessionProvider } from '@/context/SessionContext'; // Importar SessionProvider
import { ChatProvider } from '@/context/ChatContext'; // Importar ChatProvider
import { ContentSidebarProvider } from '@/context/ContentSidebarContext';
import { SearchProvider } from '@/context/SearchContext'; // Importar SearchProvider
import { UserMenuProvider } from '@/context/UserMenuContext'; // Importar UserMenuProvider
import { ChatContainer, FullChatView } from '@/components/chat'; // Importar ChatContainer y FullChatView
import { NotificationsSidebar } from '@/components/NotificationsSidebar'; // Importar NotificationsSidebar
import { CheckoutSidebar } from '@/components/CheckoutSidebar'; // Importar CheckoutSidebar
import { SearchSidebar } from '@/components/SearchSidebar'; // Importar SearchSidebar
import { UserMenuSidebar } from '@/components/UserMenuSidebar'; // Importar UserMenuSidebar
import AutoLoginPrompt from '@/components/AutoLoginPrompt'; // Importar AutoLoginPrompt
import { OrderSidebarProvider } from '@/context/OrderSidebarContext';
import { OrderSidebar } from '@/components/OrderSidebar';
import { AddressManagerProvider, useAddressManager } from '@/context/AddressManagerContext';
import { AppearanceProvider } from '@/context/AppearanceContext';
import { CategorySidebarProvider } from '@/context/CategorySidebarContext';
import { SettingsSidebarProvider } from '@/context/SettingsSidebarContext';
import { RequestRideProvider } from '@/context/RequestRideContext';
import { DriversAvailabilityProvider } from '@/context/DriversAvailabilityContext';
import { BankAccountsProvider, useBankAccounts } from '@/context/BankAccountsContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { useSession } from '@/context/SessionContext';
import { RequestRideSidebar } from '@/components/RequestRideSidebar';
import { BankAccountsSidebar } from '@/components/BankAccountsSidebar';
import SplashScreen from '@/components/SplashScreen';
// import MemberWorkPanel from "./pages/MemberWorkPanel";
import React, { Suspense } from 'react';
import ListProductPage from "@/pages/ListProductPage"; // Importar ListProductPage
import BrowseProductsPage from "@/pages/BrowseProductsPage";
import Minibid24hsPage from "@/pages/Minibid24hsPage";
import UserProfilePage from "@/pages/UserProfilePage";
import ListProductWrapper from "@/components/ListProductWrapper"; // Importar ListProductWrapper
import { SellerRoute } from "@/components/AdminOnlyRoute"; // Importar SellerRoute
import { SoundProvider } from '@/context/SoundContext';
import { MusicProvider } from '@/context/MusicContext';
import { FavoritesSidebarProvider } from '@/context/FavoritesSidebarContext';
import { FavoritesProvider } from '@/context/FavoritesContext';
import { ActivitiesSidebarProvider, useActivitiesSidebar } from '@/context/ActivitiesSidebarContext';
import { ActivitiesSidebar } from '@/components/ActivitiesSidebar';
import EditProductWrapper from "@/components/EditProductWrapper"; // Importar EditProductWrapper
import CreateNotePage from "@/pages/CreateNotePage"; // Importar CreateNotePage
import CreateNoteWrapper from "@/components/CreateNoteWrapper"; // Importar CreateNoteWrapper
import EditNoteWrapper from "@/components/EditNoteWrapper"; // NEW: Import EditNoteWrapper
import StorePublicPage from '@/pages/StorePublicPage';
import StoresPage from '@/pages/StoresPage';
import AppearanceSidebar from "@/components/AppearanceSidebar";
import SettingsSidebar from "@/components/SettingsSidebar";
import AddressManagerSidebar from "@/components/AddressManagerSidebar";
import FloatingCartButton from "@/components/FloatingCartButton";
import GeoLocationRequestModal from "@/components/GeoLocationRequestModal";
import { useState, useCallback, useEffect } from "react";
import { initFontPreloading, preWarmSupabaseConnection } from "@/lib/fontPreloader";
import { useAuctionMonitor } from "@/hooks/useAuctionMonitor";
import { debugLog } from "@/lib/debugLog";

const MODULE_RECOVERY_FLAG = 'module-import-reload-once';

const lazyImportWithSingleReload = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => {
  return React.lazy(async () => {
    try {
      const mod = await importFn();
      sessionStorage.removeItem(MODULE_RECOVERY_FLAG);
      return mod;
    } catch (error) {
      const message = String(error || '');
      const isModuleLoadError =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('not a valid JavaScript MIME type') ||
        message.includes('MIME type');

      if (isModuleLoadError && !sessionStorage.getItem(MODULE_RECOVERY_FLAG)) {
        sessionStorage.setItem(MODULE_RECOVERY_FLAG, '1');
        window.location.reload();
        return new Promise(() => undefined);
      }

      throw error;
    }
  });
};

const FollowersPage = React.lazy(() => import("@/pages/FollowersPage"));
const FollowingPage = React.lazy(() => import("@/pages/FollowingPage"));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const NoteDetailPage = React.lazy(() => import('@/pages/NoteDetailPage'));
const CollectionsPage = React.lazy(() => import('@/pages/CollectionsPage'));
const PrintRoomPage = React.lazy(() => import('@/pages/PrintRoomPage'));
const ActivityLogPage = React.lazy(() => import('@/pages/ActivityLogPage'));
const ChangasBoardPage = React.lazy(() => import('@/pages/ChangasBoardPage'));
const AdminVerificationsPage = React.lazy(() => import('@/pages/AdminVerificationsPage').then((m) => ({ default: m.AdminVerificationsPage })));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const PlayPage = lazyImportWithSingleReload(() => import('@/pages/PlayPage'));
const FayeAdminPage = lazyImportWithSingleReload(() => import('@/pages/FayeAdminPage'));
const WiFiPage = React.lazy(() => import('@/pages/WiFiPage'));
const WiFiAdminPage = React.lazy(() => import('@/pages/WiFiAdminPage'));
const PublishProductPage = React.lazy(() => import('@/pages/PublishProductPage'));
const CollaborativeProductPoolPage = React.lazy(() => import('@/pages/CollaborativeProductPoolPage'));
const TeacherDashboardPage = React.lazy(() => import('@/pages/TeacherDashboardPage'));
const CargaFacil = React.lazy(() => import('@/pages/CargaFacil'));
const NewsFeed = React.lazy(() => import('@/pages/NewsFeed'));
const ProductDetailPage = React.lazy(() => import('@/pages/ProductDetailPage'));
const SearchResultsPage = React.lazy(() => import('@/pages/SearchResultsPage'));
const SlugResolver = React.lazy(() => import('@/pages/SlugResolver'));
const CheckoutPage = React.lazy(() => import('@/pages/CheckoutPage'));
const StoreOfficePage = React.lazy(() => import('@/pages/StoreOfficePage'));
const OrderPage = React.lazy(() => import('@/pages/OrderPage'));
const MercadoPagoCallback = React.lazy(() => import('@/pages/MercadoPagoCallback'));
const queryClient = new QueryClient();
const routeFallback = (
  <div className="w-full max-w-6xl mx-auto px-2 md:px-4 pt-0 md:pt-3 overflow-x-clip">
    <div className="py-4 md:py-6 space-y-6 animate-pulse" aria-hidden="true">
      <div className="rounded-3xl border border-border/50 bg-card/70 p-4 sm:p-5 lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr,auto] gap-4 items-start">
          <div className="h-20 w-20 rounded-2xl bg-muted" />
          <div className="space-y-3 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="h-8 w-52 rounded-md bg-muted" />
              <div className="h-5 w-36 rounded-full bg-muted" />
              <div className="h-5 w-28 rounded-full bg-muted" />
            </div>
            <div className="h-4 w-full max-w-2xl rounded bg-muted" />
            <div className="h-4 w-72 rounded bg-muted" />
          </div>
          <div className="w-full sm:w-[240px] space-y-3">
            <div className="h-4 w-20 rounded bg-muted" />
            <div className="flex flex-wrap gap-2">
              <div className="h-8 w-24 rounded-xl bg-muted" />
              <div className="h-8 w-24 rounded-xl bg-muted" />
              <div className="h-8 w-24 rounded-xl bg-muted" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 overflow-hidden">
        {[1, 2, 3, 4].map((chip) => (
          <div key={chip} className="h-8 w-24 rounded-full bg-muted" />
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((card) => (
          <div key={card} className="h-72 rounded-xl bg-muted" />
        ))}
      </div>
    </div>
  </div>
);
const slugRouteFallback = null;
const radioRouteFallback = (
  <div className="min-h-[100dvh] p-4 sm:p-6 md:p-8">
    <div className="mx-auto w-full max-w-3xl space-y-4">
      <div className="h-10 w-40 animate-pulse rounded-full bg-muted" />
      <div className="rounded-2xl border border-border/60 bg-card/60 p-5 sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="h-28 w-28 animate-pulse rounded-xl bg-muted" />
          <div className="flex-1 space-y-3">
            <div className="h-7 w-3/4 animate-pulse rounded-md bg-muted" />
            <div className="h-5 w-2/3 animate-pulse rounded-md bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted" />
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card/60 p-5 sm:p-6">
        <div className="h-28 animate-pulse rounded-xl bg-muted" />
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="h-11 w-11 animate-pulse rounded-full bg-muted" />
          <div className="h-11 w-11 animate-pulse rounded-full bg-muted" />
          <div className="h-11 w-11 animate-pulse rounded-full bg-muted" />
        </div>
      </div>
    </div>
  </div>
);

class AppErrorBoundary extends React.Component<{children?: React.ReactNode}, {hasError:boolean, error?: any}> {
  constructor(props:any){ super(props); this.state = { hasError:false, error: undefined }; }
  static getDerivedStateFromError(err:any){ return { hasError:true, error:err }; }
  componentDidCatch(err:any, info:any){ console.error('App ErrorBoundary', err, info); }
  render(){ if(this.state.hasError){
    return (<div className="p-6 text-red-600"><h3 className="font-semibold">Error en la aplicación</h3><pre className="whitespace-pre-wrap">{String(this.state.error)}</pre></div>);
  } return this.props.children as any; }
}

// Enhanced error boundary for module loading errors with auto-recovery
class ModuleErrorBoundary extends React.Component<{children?: React.ReactNode}, {hasError:boolean, error?: any, retryCount:number}> {
  constructor(props:any){ 
    super(props); 
    this.state = { hasError:false, error: undefined, retryCount: 0 }; 
  }
  
  static getDerivedStateFromError(err:any){ 
    console.error('[ModuleErrorBoundary] Error caught:', err);
    return { hasError:true, error:err }; 
  }
  
  componentDidCatch(err:any, info:any){ 
    console.error('[ModuleErrorBoundary] Error details:', err, info);
    // Check if it's a module loading error
    const isModuleError = String(err).includes('import') || 
                          String(err).includes('module') ||
                          String(err).includes('Failed to fetch') ||
                          String(err).includes('Importing a module script failed') ||
                          String(err).includes('not a valid JavaScript MIME type') ||
                          String(err).includes('MIME type');

    if (isModuleError && !sessionStorage.getItem(MODULE_RECOVERY_FLAG)) {
      console.warn('[ModuleErrorBoundary] Attempting hard reload auto-recovery...');
      sessionStorage.setItem(MODULE_RECOVERY_FLAG, '1');
      window.location.reload();
      return;
    }

    if (isModuleError && this.state.retryCount < 3) {
      console.warn('[ModuleErrorBoundary] Attempting soft auto-recovery...');
      setTimeout(() => {
        this.setState(prev => ({ retryCount: prev.retryCount + 1, hasError: false, error: undefined }));
      }, 1000);
    }
  }
  
  render(){ 
    if(this.state.hasError){
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-background p-6 gap-4">
          <div className="text-center">
            <h3 className="font-bold text-lg text-destructive mb-2">Error en la aplicación</h3>
            <p className="text-sm text-muted-foreground mb-4">No se pudo cargar el módulo</p>
            {this.state.retryCount > 0 && (
              <p className="text-xs text-muted-foreground mb-4">Intentos de recuperación: {this.state.retryCount}/3</p>
            )}
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90"
              >
                Recargar página
              </button>
              <button 
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md text-sm font-medium hover:opacity-90"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children as any; 
  }
}

function GlobalSidebars() {
  // PERFORMANCE: Initialize font preloading on component mount (one-time)
  useEffect(() => {
    initFontPreloading();
    preWarmSupabaseConnection();
  }, []);

  // Monitor auctions and close ended ones automatically
  useAuctionMonitor();

  return <>
    <AppearanceSidebar />
    <SettingsSidebar />
    <AddressManagerSidebar />
  </>;
}

function ThemeColorHydrator() {
  const { profile } = useSession();
  
  useEffect(() => {
    const themeColor = profile?.appearance_settings?.theme_color || '#3b82f6'; // default blue
    
    // Convert hex to HSL
    const r = parseInt(themeColor.slice(1, 3), 16) / 255;
    const g = parseInt(themeColor.slice(3, 5), 16) / 255;
    const b = parseInt(themeColor.slice(5, 7), 16) / 255;
    
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    
    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    
    // Update CSS variables
    document.documentElement.style.setProperty('--theme-identity-h', h.toString());
    document.documentElement.style.setProperty('--theme-identity-s', s.toString() + '%');
    document.documentElement.style.setProperty('--theme-identity-l', l.toString() + '%');
    
    // Create light background variations
    document.documentElement.style.setProperty('--theme-bg-l', '96%');
    document.documentElement.style.setProperty('--theme-bg-s', '33%');
    
  }, [profile?.appearance_settings?.theme_color]);

  return null;
}

// Componente para orquestar el flujo de primera visita
function FirstVisitOrchestrator() {
  // DISABLED TEMPORARILY: App won't start due to React error #31
  // Re-enable once React error is fixed
  return null;
}

function BankAccountsSidebarWrapper() {
  const { isOpen, mode, entityId, close } = useBankAccounts();
  const { session } = useSession();
  
  if (!session?.user?.id || !entityId) return null;

  return (
    <BankAccountsSidebar
      entityId={entityId}
      mode={mode}
      isOpen={isOpen}
      onClose={close}
      title="Tus cuentas bancarias"
    />
  );
}

function ActivitiesSidebarFromContext() {
  const { isOpen, closeSidebar } = useActivitiesSidebar();
  
  return (
    <ActivitiesSidebar
      isOpen={isOpen}
      onClose={closeSidebar}
    />
  );
}

const AppContent = () => {
  const location = useLocation();
  const isChatRoute = location.pathname === '/chat';

  return (
    <AppErrorBoundary>
      <SplashScreen />
      <AppNotificationProvider>
      <CheckoutProvider>
      <SearchProvider>
      <UserMenuProvider>
      <RequestRideProvider>
    <OrderSidebarProvider>
    <ContentSidebarProvider>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/otp-login" element={<OtpLoginPage />} />
        <Route path="/admin/verifications" element={<Suspense fallback={routeFallback}><AdminVerificationsPage /></Suspense>} />
        {/* Rutas autenticadas */}
        <Route element={<MainLayout />}>
          <Route path="/notas" element={<Suspense fallback={routeFallback}><NewsFeed /></Suspense>} />
          <Route path="/notas/de-seguidos" element={<Suspense fallback={routeFallback}><NewsFeed /></Suspense>} />
          <Route path="/notas/redactar" element={<CreateNotePage session={null} />} />
          <Route path="/buscar" element={<Minibid24hsPage />} />
          <Route path="/seguidos" element={<Suspense fallback={routeFallback}><FollowingPage /></Suspense>} />
          <Route path="/followers" element={<Suspense fallback={routeFallback}><FollowersPage /></Suspense>} />
          <Route path="/tiendas" element={<BrowseProductsPage />} />
          <Route path="/tiendas/comunidad" element={<BrowseProductsPage />} />
          <Route path="/tiendas/mis-tiendas" element={<Suspense fallback={routeFallback}><StoresPage /></Suspense>} />
          <Route path="/24hs" element={<Minibid24hsPage />} />
          <Route path="/radio" element={<Suspense fallback={radioRouteFallback}><PlayPage /></Suspense>} />
          <Route path="/radio/admin" element={<SellerRoute allowedRoles={['admin']}><Suspense fallback={routeFallback}><FayeAdminPage /></Suspense></SellerRoute>} />
          <Route path="/wifi" element={<Suspense fallback={routeFallback}><WiFiPage /></Suspense>} />
          <Route path="/wifi/admin" element={<Suspense fallback={routeFallback}><WiFiAdminPage /></Suspense>} />
          <Route path="/tiendas/cargar" element={<SellerRoute><Suspense fallback={routeFallback}><CollaborativeProductPoolPage /></Suspense></SellerRoute>} />
          <Route path="/tiendas/pool-productos" element={<SellerRoute><Suspense fallback={routeFallback}><CollaborativeProductPoolPage /></Suspense></SellerRoute>} />
          <Route path="/teacher-dashboard" element={<Suspense fallback={routeFallback}><TeacherDashboardPage /></Suspense>} />
          <Route path="/create-note" element={<CreateNoteWrapper />} />
          <Route path="/edit-note/:noteId" element={<EditNoteWrapper />} /> {/* NEW */}
          <Route path="/products/:productId" element={<Suspense fallback={routeFallback}><ProductDetailPage /></Suspense>} />
          <Route path="/edit-product/:productId" element={<EditProductWrapper />} />
          <Route path="/collections" element={<Suspense fallback={routeFallback}><CollectionsPage /></Suspense>} />
          <Route path="/notas/:noteId" element={<Suspense fallback={routeFallback}><NoteDetailPage /></Suspense>} />
          <Route path="/notificaciones" element={<Suspense fallback={routeFallback}><NotificationsPage /></Suspense>} />
          {/* <Route path="/miembro-trabajo" element={<MemberWorkPanel />} /> */}
          <Route path="/orden/:orderId" element={<Suspense fallback={routeFallback}><OrderPage /></Suspense>} />
          <Route path="/print-room" element={<Suspense fallback={routeFallback}><PrintRoomPage /></Suspense>} />
          <Route path="/activity-log" element={<Suspense fallback={routeFallback}><ActivityLogPage /></Suspense>} />
          <Route path="/configuracion" element={<Suspense fallback={routeFallback}><SettingsPage /></Suspense>} />
          <Route path="/cargafacil" element={<Suspense fallback={routeFallback}><CargaFacil /></Suspense>} />
          <Route path="/transporte" element={<ModuleErrorBoundary><Suspense fallback={routeFallback}><ChangasBoardPage /></Suspense></ModuleErrorBoundary>} />
          <Route path="/search-results/:searchTerm?" element={<Suspense fallback={routeFallback}><SearchResultsPage /></Suspense>} />
          {/* Deprecated standalone geographic-news route removed */}
          <Route path="/:storeSlug/tablero" element={<Suspense fallback={routeFallback}><StoreOfficePage /></Suspense>} />
          <Route path="/checkout" element={<Suspense fallback={routeFallback}><CheckoutPage /></Suspense>} />
          <Route path="/mercadopago/callback" element={<Suspense fallback={routeFallback}><MercadoPagoCallback /></Suspense>} />
          <Route path="/chat" element={<FullChatView />} />
          {/* Legacy /mis-tiendas route removed in favor of /tiendas/mis-tiendas */}
          <Route path="/:slug" element={<Suspense fallback={slugRouteFallback}><SlugResolver /></Suspense>} />
        </Route>
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<Suspense fallback={routeFallback}><NotFound /></Suspense>} />
      </Routes>
      {/* Hide ChatContainer on /chat route */}
      {!isChatRoute && <ChatContainer />}
      {/* NotificationsSidebar */}
      <NotificationsSidebar />
      {/* SearchSidebar */}
      <SearchSidebar />
      {/* CheckoutSidebar */}
      <CheckoutSidebar />
      {/* Floating Cart Button - globally available */}
      <FloatingCartButton />
      {/* UserMenuSidebar */}
      <UserMenuSidebar />
      {/* Auto Login Prompt - abre sidebar de login después de 5 segundos si no está logueado */}
      <AutoLoginPrompt />
      {/* RequestRideSidebar */}
      <RequestRideSidebar />
      {/* ActivitiesSidebar */}
      <ActivitiesSidebarFromContext />
      {/* BankAccountsSidebar */}
      <BankAccountsSidebarWrapper />
      <FirstVisitOrchestrator />
      {/* GeoLocationRequestModal removed - location prompt disabled */}
      </ContentSidebarProvider>
      </OrderSidebarProvider>
      </RequestRideProvider>
      </UserMenuProvider>
      </SearchProvider>
      </CheckoutProvider>
      </AppNotificationProvider>
      <GlobalSidebars />
      <ThemeColorHydrator />
    </AppErrorBoundary>
  );
};

const App = () => {
  debugLog('app', 'Componente App renderizado.');
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="auto" storageKey="vite-ui-theme" attribute="class" enableSystem>
        <TooltipProvider>
          <Toaster />
          <ServiceWorkerUpdateToast />
          <SessionProvider>
            <ChatProvider maxWindows={3}>
              <AddressManagerProvider>
                <AppearanceProvider>
                  <BankAccountsProvider>
                    <CategorySidebarProvider>
                      <SettingsSidebarProvider>
                        <SoundProvider>
                          <MusicProvider>
                            <FavoritesProvider>
                              <FavoritesSidebarProvider>
                              <ActivitiesSidebarProvider>
                                <DriversAvailabilityProvider>
                                <BrowserRouter
                                future={{
                                  v7_startTransition: true,
                                  v7_relativeSplatPath: true,
                                }}
                              >
                                <AppContent />
                                </BrowserRouter>
                                </DriversAvailabilityProvider>
                              </ActivitiesSidebarProvider>
                            </FavoritesSidebarProvider>
                            </FavoritesProvider>
                          </MusicProvider>
                        </SoundProvider>
                      </SettingsSidebarProvider>
                    </CategorySidebarProvider>
                  </BankAccountsProvider>
                </AppearanceProvider>
              </AddressManagerProvider>
            </ChatProvider>
          </SessionProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;